// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import CartPageTemplate, { CartPageTemplateStyles } from "@plumberssupply/App/Components/Cart/CartPageTemplate";
import XngZone from "@plumberssupply/App/Components/Common/XngZone";
import Notify from "@plumberssupply/App/Components/Notify";
import XngSticky from "@plumberssupply/App/Components/XngSticky";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { CheckoutStepper } from "@plumberssupply/App/Widgets/CheckoutStepper/CheckoutStepper";
import { StepperContext } from "@plumberssupply/App/Widgets/CheckoutStepper/StepperContext";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showCheckoutStepper = "showCheckoutStepper",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showCheckoutStepper]: boolean;
    };
}

type Props = OwnProps;

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {
    headerTitle?: TypographyPresentationProps;
}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {
    container: {
        css: css`
            min-height: 80vh;
        `,
    },
    headerTitle: {
        variant: "h2",
        color: "common.ui11",
        css: css`
            margin-bottom: 15px;
        `,
    },
    headerSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            width: 100%;
            padding: 0;
            flex-wrap: wrap;
            border-bottom: 1px solid ${color("common.ui7")};
            margin-bottom: 16px;
        `,
    },
    totalSection: {
        css: css`
            width: 308px;

            ${xScreen("lte", "md")} {
                width: 260px;
            }

            ${xScreen("lte", 767)} {
                width: 100%;
                display: flex;
                justify-content: center;
                ${({ currentStep }: any) => (currentStep === 3 ? `order: 2;` : `order: 4; margin-top: 32px;`)}
                margin-bottom: 32px;

                & > div.xng-sticky {
                    max-width: 100%;
                    width: 100%;
                }
            }
        `,
    },
};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({ id, fields: { title, showCheckoutStepper } }) => {
    const { currentStep } = useContext(StepperContext);
    const {
        cartState,
        isCheckoutMobileScreen,
        isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired,
        billToState,
        shipToState,
    } = useAppSelector(state => {
        const { cartId } = state.pages.checkoutReviewAndSubmit;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        const billToState = getBillToState(state, cartState.value?.billToId);
        const shipToState = getShipToState(state, cartState.value?.shipToId);

        return {
            cartState,
            isCheckoutMobileScreen: getXngCommon(state).isCheckoutMobileScreen,
            isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
            isBillingAddressUpdateRequired: state.pages.checkoutShipping.isBillingAddressUpdateRequired,
            billToState,
            shipToState,
        };
    });

    return (
        <CartPageTemplate extendedStyles={styles}>
            {isCheckoutMobileScreen && (
                <CartPageTemplate.HeaderSection>
                    {title && <Typography {...styles.headerTitle}>{title}</Typography>}
                    {showCheckoutStepper && <CheckoutStepper />}
                </CartPageTemplate.HeaderSection>
            )}
            <CartPageTemplate.MainSection>
                {!isCheckoutMobileScreen && (
                    <CartPageTemplate.HeaderSection>
                        {title && <Typography {...styles.headerTitle}>{title}</Typography>}
                        {showCheckoutStepper && <CheckoutStepper />}
                    </CartPageTemplate.HeaderSection>
                )}
                <XngZone contentId={id} zoneName="Content0" hint="Zone For Header (Cart Approval message)" />
                <XngZone
                    contentId={id}
                    zoneName="Content1"
                    hint="Zone For Step Content (Shipping container, Submit & Review container)"
                />
            </CartPageTemplate.MainSection>
            <StyledWrapper {...({ currentStep } as any)} {...styles.totalSection}>
                <XngSticky isCheckoutPge={true}>
                    <XngZone contentId={id} zoneName="Content2" hint="Zone For Checkout Total" />
                    {!cartState.isLoading && (isShippingAddressUpdateRequired || !shipToState.value) && (
                        <Notify
                            css={css`
                                margin: 12px 0 0 0;
                            `}
                            type="warning"
                        >
                            {siteMessage("Plumbers_Shipping_Address_Update_Required")}
                        </Notify>
                    )}
                    {!cartState.isLoading && (isBillingAddressUpdateRequired || !billToState.value) && (
                        <Notify
                            css={css`
                                margin: 12px 0 0 0;
                            `}
                            type="warning"
                        >
                            {siteMessage("Plumbers_Billing_Address_Update_Required")}
                        </Notify>
                    )}
                    <XngZone contentId={id} zoneName="Content4" hint="Zone" />
                </XngSticky>
            </StyledWrapper>
            <CartPageTemplate.ActionsSection>
                <XngZone contentId={id} zoneName="Content3" hint="Zone For Stepper Actions" />
            </CartPageTemplate.ActionsSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutPageContainer,
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage", "ExpressCheckoutPage"] as any,
        displayName: "Checkout Page Container",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "General",
                displayName: "Title",
                defaultValue: "Title",
            },
            {
                name: fields.showCheckoutStepper,
                displayName: "Show Checkout Stepper",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
